import React from "react";
import dynamic from "next/dynamic";
import Head from "next/head";
import Link from "next/link";

import FAQContactForm from "components/FAQContactForm";
import TextLink from "components/link/Textlink";
import { MarkdownViewer } from "dynamic/components/MarkdownViewer";
import { FAQ_PAGE_PATH } from "dynamic/routes";
import { buildFAQAnswers } from "routes/buildFAQ";
import buildCanonicalTagHref from "utils/seo/buildCanonicalTagHref";

import config from "../../../../config";

const TrustpilotBox = dynamic(() => import("layout/TrustpilotBox"), { ssr: false });

function FAQAnswer({
  title, content, relatedItems, pageUrl,
}) {
  return (
    <>
      <Head>
        <meta name="description" content={`Häufig gestellte Fragen: ${title}`} />
        <meta
          name="robots"
          content={config.robotsContent}
        />
        <title>{title}</title>

        <link
          rel="canonical"
          href={buildCanonicalTagHref(`${FAQ_PAGE_PATH}/${pageUrl}`)}
        />
      </Head>

      <div className="bg-grey-lighter border-b pt-10 md:pt-12 pb-12 lg:pb-24">
        <div className="w-full max-w-lg mx-auto px-3 md:px-5">
          <div className="px-6 lg:px-12 mb-5">
            <TextLink to="/faq" back small>
              zurück zur FAQ Übersicht
            </TextLink>
          </div>

          <div className="faq-content bg-white rounded-sm border p-6 lg:p-12 mb-12 lg:mb-20">
            <h1 className="headline--5 mb-6">{title}</h1>
            <MarkdownViewer content={content} />
          </div>

          <div className="px-6 lg:px-12">
            <h2 className="headline--5 mb-3">Verwandte Fragen</h2>
            <ul className="list-reset">
              {relatedItems.map(({ pageUrl, title }) => (
                <li key={pageUrl} className="mb-3">
                  <Link href={pageUrl}>
                    <a
                      className="link link--secondary font-normal"
                      href={pageUrl}
                    >
                      {title}
                    </a>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Contact form */}
      <div className="py-15 lg:py-30 border-b">
        <div className="page-container">
          <div className="w-full md:w-3/5 mx-auto max-w-md">
            <h2 className="headline--4 text-center mb-6">
              Konnten wir Ihre Frage nicht beantworten?
            </h2>
            <div className="mb-10 text-center px-5">
              Sie können uns Mo&ndash;Fr unter der Rufnummer
              {" "}
              <a
                className="link link--primary font-normal"
                href="tel:+493081097600"
              >
                030&nbsp;810976&nbsp;00
              </a>
              {" "}
              von 9&ndash;18 Uhr konktaktieren.
              <div className="contact-option">oder</div>
              Sie erreichen uns jederzeit über unser Kontaktformular. Wir melden
              uns dann in der Regel innerhalb von 24h zurück.
            </div>
            <p className="hidden lg:block bg-grey-lighter py-4 pl-5 pr-3 md:pr-5 border border-grey rounded-t uppercase font-bold">
              Kontaktformular
            </p>
            <FAQContactForm />
          </div>
        </div>
      </div>
      <div className="py-10 border-b">
        <div className="text-center">
          <TrustpilotBox />
        </div>
      </div>
    </>
  );
}

export async function getStaticPaths() {
  const paths = buildFAQAnswers().map((faq) => ({
    params: { slug: faq.pageUrl },
  }));

  return {
    paths,
    fallback: false,
  };
}

export async function getStaticProps(context) {
  const {
    title,
    content,
    relatedItems,
    pageUrl,
  } = buildFAQAnswers().find(
    (faq) => faq.pageUrl === context.params.slug,
  );

  return {
    props: {
      title,
      content,
      relatedItems,
      pageUrl,
    },
  };
}

export default FAQAnswer;
